import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { brainOrg } from '../data/legalApi';
import './Brain.css';

// Organization knowledge base — every project the user can see, with what
// its brain holds and whether it is current. The per-project detail (chunks
// by document, gaps, rebuild) lives on the project's Knowledge tab.

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const bs = (s) => ({ ready: 'b-ok', pending: 'b-info', none: 'b-muted', error: 'b-danger' }[s] || 'b-muted');
const SOURCE_LABEL = { deal: 'overview', ecosystem: 'ecosystem', clause: 'clauses', artifact: 'artifact clauses', profile: 'profiles', analysis: 'analyses', correspondence: 'correspondence', revision: 'revisions', dealPoint: 'deal points' };

export default function Knowledge() {
  const { orgId, org } = useOrg();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [qText, setQ] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!orgId) return;
    setData(null);
    brainOrg(orgId).then(setData).catch((e) => setErr(e.message));
  }, [orgId]);

  const projects = useMemo(() => (data?.projects || [])
    .filter((p) => !filter || (filter === 'gaps' ? (p.brainStatus !== 'ready' || !p.hasObjective) : p.brainStatus === filter))
    .filter((p) => !qText || p.name.toLowerCase().includes(qText.toLowerCase()) || (p.tags || []).some((t) => t.toLowerCase().includes(qText.toLowerCase()))), [data, filter, qText]);

  const totals = useMemo(() => (data?.projects || []).reduce((acc, p) => {
    acc.chunks += p.chunkCount; acc.docs += p.docCount; acc[p.brainStatus] = (acc[p.brainStatus] || 0) + 1;
    if (!p.hasObjective) acc.noObjective += 1;
    for (const [s, n] of Object.entries(p.sources || {})) acc.sources[s] = (acc.sources[s] || 0) + n;
    return acc;
  }, { chunks: 0, docs: 0, noObjective: 0, sources: {} }), [data]);

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <div>
          <h1>Knowledge</h1>
          <p className="cs-help" style={{ margin: 0 }}>What {org?.name || 'this organization'} has taught its projects: each project's brain indexes its documents, deal points, analyses, and correspondence, and answers questions with citations. This page shows what is in, what is stale, and what is missing.</p>
        </div>
      </div>

      {err && <div className="cs-banner b-danger">{err}</div>}
      {!data && !err && <div className="cs-empty">Reading project brains…</div>}

      {data && (
        <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
          <div className="brain-strip">
            <div className="cs-stat is-compact"><span className="cs-stat-label">Projects</span><span className="cs-stat-value">{data.projects.length}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">Documents</span><span className="cs-stat-value">{totals.docs}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">Indexed chunks</span><span className="cs-stat-value">{totals.chunks.toLocaleString()}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">Brains ready</span><span className="cs-stat-value" style={{ color: 'var(--ok)' }}>{totals.ready || 0}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">Rebuild pending</span><span className="cs-stat-value" style={{ color: totals.pending ? 'var(--info)' : undefined }}>{totals.pending || 0}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">Never built</span><span className="cs-stat-value" style={{ color: totals.none ? 'var(--warn)' : undefined }}>{totals.none || 0}</span></div>
            <div className="cs-stat is-compact"><span className="cs-stat-label">No structure note</span><span className="cs-stat-value" style={{ color: totals.noObjective ? 'var(--warn)' : undefined }}>{totals.noObjective}</span></div>
          </div>

          {Object.keys(totals.sources).length > 0 && (
            <div className="kb-sources u-small">
              <span className="u-muted">Across the org:</span>
              {Object.entries(totals.sources).sort((a, b) => b[1] - a[1]).map(([s, n]) => <span key={s} className="cs-badge b-muted">{SOURCE_LABEL[s] || s} {n}</span>)}
            </div>
          )}

          <div className="brain-filters">
            <input className="cs-input" placeholder="Search projects or tags…" value={qText} onChange={(e) => setQ(e.target.value)} aria-label="Search projects" />
            {[['', 'All'], ['ready', 'Ready'], ['pending', 'Pending'], ['none', 'Never built'], ['gaps', 'Needs attention']].map(([k, l]) => (
              <button type="button" key={k || 'all'} className={`cs-chip${filter === k ? ' selected' : ''}`} onClick={() => setFilter(k)}>{l}</button>
            ))}
            <span className="u-small u-muted">{projects.length} shown</span>
          </div>

          <div className="u-scroll-x">
            <table className="cs-table">
              <thead><tr><th>Project</th><th>Type</th><th>Brain</th><th className="num">Chunks</th><th className="num">Docs</th><th className="num">Open points</th><th>Structure note</th><th>Last indexed</th></tr></thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/deals/${p.id}?tab=knowledge`}><strong>{p.name}</strong></Link><div className="u-xs u-muted">{(p.tags || []).join(' · ')}</div></td>
                    <td className="u-small u-muted">{p.projectType}</td>
                    <td><span className={`cs-badge ${bs(p.brainStatus)}`}>{p.brainStatus}</span></td>
                    <td className="num u-tabular">{p.chunkCount}</td>
                    <td className="num u-tabular">{p.docCount}</td>
                    <td className="num u-tabular">{p.openPointCount}</td>
                    <td>{p.hasObjective ? <span className="cs-badge b-ok">set</span> : <span className="cs-badge b-warn">missing</span>}</td>
                    <td className="u-small u-muted">{fmtDate(p.brainBuiltAt)}</td>
                  </tr>
                ))}
                {!projects.length && <tr><td colSpan={8} className="u-muted">No projects match.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
